import { IRate } from '@core/models/Rate/Rate.interface';
import { PrepaidRoomCluster } from './../models/PrepaidRoomCluster/PrepaidRoomCluster.model';
import { SearchStateService } from './search-state.service';
import { Injectable } from '@angular/core';
import { ReqService } from '@core/services/req.service';

@Injectable()
export class PriceService {
  public totalNights: number;
  public totalRooms: number;
  private isLanding: boolean;
  private showFullPrice: any;

  constructor(
    private searchStateService: SearchStateService,
    private readonly contextService: ReqService,
  ) {
    this.searchStateService.stateUpdated$.subscribe((isUpdated) => {
      if (isUpdated) {
        const data = this.getData();
        this.totalNights = data.nights;
        this.totalRooms = data.rooms;
        this.showFullPrice = data.showFullPrice;
      }
    });
    this.isLanding = this.contextService.isLanding;
  }

  /**
   * Get the correct price by filter selected per night or total
   * @return {number}
   */
  public getPrice(
    rate: IRate,
    priceBy,
    showAmountTotal,
    isDisney: boolean = false,
    ticketsCost?: number,
  ) {
    if (rate) {
      if (this.showFullPrice === 'true' && rate.price.total_with_service_fee) {
        if (isDisney) {
          ticketsCost = ticketsCost ? ticketsCost : 0;
          return priceBy === 'TOTAL'
            ? rate.price.show_amount
            : (rate.price.show_amount - ticketsCost) / this.totalNights / this.totalRooms;
        } else {
          const roomOnlyCost = ticketsCost ? ticketsCost : 0;
          return priceBy === 'TOTAL'
            ? rate.price.total_with_service_fee
            : (rate.price.total_with_service_fee - roomOnlyCost) /
                this.totalNights /
                this.totalRooms;
        }
      } else {
        if (priceBy === 'TOTAL') {
          if (rate.payment_method.code === 'PREPAID') {
            return showAmountTotal || typeof showAmountTotal === 'undefined'
              ? rate.price.total
              : rate.price.show_amount;
          } else {
            return rate.price.total;
          }
        } else {
          if (rate.payment_method.code === 'PREPAID') {
            return showAmountTotal
              ? rate.price.price_per_night_per_room
              : rate.price.show_amount_per_night_per_room;
          } else {
            return rate.price.price_per_night_per_room;
          }
        }
      }
    }
  }

  public getMapPrice(price, priceBy) {
    if (this.showFullPrice && price.hasOwnProperty('service_fee')) {
      return priceBy === 'TOTAL'
        ? price.total + price.service_fee
        : (price.total + price.service_fee) / this.totalNights / this.totalRooms;
    } else {
      return priceBy === 'TOTAL' ? price.total : price.per_night;
    }
  }

  /**
   * Get the correct price without discount by filter selected per night or total
   * @return {number}
   */
  public getPriceWithoutDiscount(rate, priceBy, showAmountTotal) {
    if (this.showFullPrice && rate.price.service_fee) {
      return priceBy === 'TOTAL'
        ? rate.price.price_without_discount + rate.price.service_fee
        : (rate.price.price_without_discount + rate.price.service_fee) /
            this.totalNights /
            this.totalRooms;
    } else {
      if (priceBy === 'TOTAL') {
        if (rate.payment_method.code === 'PREPAID') {
          return showAmountTotal || typeof showAmountTotal === 'undefined'
            ? rate.price.price_without_discount
            : rate.price.show_amount_without_discount;
        } else {
          return rate.price.price_without_discount;
        }
      } else {
        if (rate.payment_method.code === 'PREPAID') {
          return showAmountTotal
            ? rate.price.price_per_night_per_room_without_discount
            : rate.price.show_amount_per_night_per_room_without_discount;
        } else {
          return rate.price.price_per_night_per_room_without_discount;
        }
      }
    }
  }

  public getMapPriceWithoutDiscount(price, priceBy) {
    if (this.showFullPrice && price.service_fee) {
      return priceBy === 'TOTAL'
        ? price.total_without_discount + price.service_fee
        : (price.total_without_discount + price.service_fee) /
            this.totalNights /
            this.totalRooms;
    } else {
      return priceBy === 'TOTAL'
        ? price.total_without_discount
        : price.price_without_discount;
    }
  }

  public getData() {
    let rooms = 1;
    let nights = 1;
    if (!this.isLanding) {
      const dates = this.searchStateService.getDate()
        ? this.searchStateService.getDate().split(',')
        : '';
      const diff = Math.abs(new Date(dates[1]).valueOf() - new Date(dates[0]).valueOf());
      rooms = this.searchStateService.getDecodedRooms().rooms.length;
      nights = diff / (1000 * 60 * 60 * 24);
    }
    return {
      rooms: rooms,
      nights: nights,
      showFullPrice: this.searchStateService.getLocationSearch().withFullPrice,
    };
  }

  public filterRefundableRooms(rooms: PrepaidRoomCluster[] = [], isDisney): any {
    const filteredRooms = [];
    if (!isDisney) {
      rooms.forEach((room) => {
        const filteredRates = [];
        room.rates.forEach((rate) => {
          if (rate.refundable) {
            filteredRates.push(rate);
          }
        });
        if (filteredRates.length) {
          filteredRooms.push({ ...room, rates: filteredRates });
        }
      });
      return filteredRooms;
    } else {
      return rooms;
    }
  }

  private getBestByPaymentMethodAndRefundable(
    paymentMethod,
    refundable,
    hotelAvailabilityInfo,
  ) {
    let bestAvailable;

    if (paymentMethod === 'PREPAID') {
      bestAvailable = refundable
        ? hotelAvailabilityInfo.refundable_prepaid_room_clusters[0]
        : hotelAvailabilityInfo.prepaid_room_clusters[0];
    } else if (paymentMethod === 'PAY_AT_DESTINATION') {
      bestAvailable = refundable
        ? hotelAvailabilityInfo.refundable_paid_at_destination_room_clusters[0]
        : hotelAvailabilityInfo.paid_at_destination_room_clusters[0];
    } else {
      if (refundable) {
        bestAvailable = hotelAvailabilityInfo.all_refundable_room_clusters.sort(
          (room1, room2) => {
            return (
              room1.rates[0].price.price_per_night_per_room -
              room2.rates[0].price.price_per_night_per_room
            );
          },
        )[0];
      } else {
        bestAvailable = hotelAvailabilityInfo.best_room_cluster;
      }
    }
    return bestAvailable;
  }

  public shouldShowPaymentsOptionsAlert(
    bestAvailable,
    priceByValue,
    hotelAvailabilityInfo,
  ) {
    const paymentMethod = this.searchStateService.getPaymentMethod();
    let bestByPaymentMethod = null;
    let cheapest_price = null;
    if (paymentMethod !== 'all') {
      const bestPriceToShow = this.getPrice(
        bestAvailable.rates[0],
        priceByValue,
        hotelAvailabilityInfo.show_amount_total,
      );
      if (paymentMethod === 'PREPAID') {
        bestByPaymentMethod = this.getBestByPaymentMethodAndRefundable(
          'PAY_AT_DESTINATION',
          false,
          hotelAvailabilityInfo,
        );
      } else {
        bestByPaymentMethod = this.getBestByPaymentMethodAndRefundable(
          'PREPAID',
          false,
          hotelAvailabilityInfo,
        );
      }
      cheapest_price = this.getPrice(
        bestByPaymentMethod.rates[0],
        priceByValue,
        hotelAvailabilityInfo.show_amount_total,
      );

      if (cheapest_price < bestPriceToShow) {
        return { value: true, type: bestByPaymentMethod.rates[0].payment_method.code };
      }
    }
  }

  public getRate(availability: any, paymentMethod: any, refundable: boolean) {
    const bestRate = this.getBestByPaymentMethodAndRefundable(
      paymentMethod,
      refundable,
      availability,
    );
    return bestRate;
  }
}
